import { FC, PropsWithChildren, ReactNode } from 'react';

import {
    CalendarDays,
    CheckCircle2,
    CircleDollarSign,
    Clock3,
    Info,
    ShieldCheck,
} from 'lucide-react';

import { Button } from '../ui/Button';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/Dialog';

const DetailCard: FC<
    PropsWithChildren<{
        icon: ReactNode;
        title: string;
        success?: boolean;
    }>
> = ({ icon, title, success = false, children }) => (
    <section
        className={
            success
                ? 'rounded-xl border border-green-500/30 bg-green-500/5 p-4'
                : 'rounded-xl border bg-muted/20 p-4'
        }
    >
        <div className="flex items-start gap-3">
            {icon}
            <div>
                <h3 className="font-semibold">{title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    {children}
                </p>
            </div>
        </div>
    </section>
);

type Props = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    startDate: string;
    threshold: string;
    percentage: number;
    maximumFee: string;
};

export const ServiceFeeDetailsDialog: FC<Props> = ({
    open,
    onOpenChange,
    startDate,
    threshold,
    percentage,
    maximumFee,
}) => (
    <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] max-w-2xl overflow-y-auto">
            <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-left text-xl">
                    <CircleDollarSign className="h-5 w-5 text-primary" />
                    Сервісний збір CartOn
                </DialogTitle>
                <DialogDescription className="text-left leading-relaxed">
                    З {startDate} сервісний збір допомагатиме нам підтримувати
                    платформу, розвивати нові можливості та покращувати безпеку
                    угод.
                </DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
                <DetailCard
                    icon={
                        <CalendarDays className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                    }
                    title="Для яких замовлень"
                >
                    Збір застосовується до окремого замовлення продавця,
                    створеного з {startDate}, якщо вартість товарів перевищує{' '}
                    {threshold}. Для суми {threshold} включно збору немає, а
                    раніше створені замовлення залишаються без збору.
                </DetailCard>

                <DetailCard
                    icon={
                        <CircleDollarSign className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                    }
                    title="Як розраховується"
                >
                    {percentage}% від вартості товарів у замовленні, але не
                    більше {maximumFee}. Якщо покупка містить товари кількох
                    продавців, збір розраховується окремо для кожного їхнього
                    замовлення.
                </DetailCard>

                <DetailCard
                    icon={
                        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                    }
                    title="Коли списується"
                >
                    Один раз із балансу продавця після успішного завершення
                    замовлення. За скасовані, відхилені або повернуті замовлення
                    збір не списується.
                </DetailCard>

                <DetailCard
                    icon={
                        <Clock3 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                    }
                    title="Якщо коштів недостатньо"
                >
                    Збір переходить у статус очікування. Після поповнення
                    балансу кошти автоматично спрямовуються на найстаріші
                    несплачені збори.
                </DetailCard>

                <DetailCard
                    icon={
                        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
                    }
                    title="Для покупця нічого не змінюється"
                    success
                >
                    Сервісний збір сплачує продавець. Він не додається до суми
                    замовлення покупця.
                </DetailCard>

                <div className="flex items-start gap-2 rounded-xl bg-muted/50 p-3 text-xs leading-relaxed text-muted-foreground">
                    <Info className="mt-0.5 h-4 w-4 shrink-0" />
                    Точний розмір і статус збору відображатимуться в картці
                    продажу та в історії операцій балансу.
                </div>
            </div>

            <DialogFooter>
                <DialogClose asChild>
                    <Button type="button">Зрозуміло</Button>
                </DialogClose>
            </DialogFooter>
        </DialogContent>
    </Dialog>
);
